import React, { useState } from "react"
import { useMutation } from "urql"

import { AnimatedModal } from "../../../components/shared/Modal"
import { useToast } from "../../../contexts/ToastContext"

import { DISCONNECT_CALENDAR_CONNECTION_MUTATION } from "./graphql"
import { disconnectWarning, providerLabel } from "./selectors"
import { CalendarConnectionSummary, CalendarMigrationStatus, MutationResultPayload } from "./types"

interface Props {
  connection: CalendarConnectionSummary | null
  migrations: CalendarMigrationStatus[]
  onClose: () => void
  onDisconnected: () => void
}

// Disconnect confirmation via disconnectCalendarConnection. The backend also
// removes the underlying Cronofy profile/feed for legacy connections, so this
// single mutation covers both implementations. Warns when a migration is
// active for the connection; pending remote writes are not exposed by the
// status payload yet, so no fake safety claim is made about them.
const DisconnectConfirmModal = ({ connection, migrations, onClose, onDisconnected }: Props) => {
  const [submitting, setSubmitting] = useState(false)
  const [, disconnectConnection] = useMutation<{ disconnectCalendarConnection: MutationResultPayload }>(
    DISCONNECT_CALENDAR_CONNECTION_MUTATION
  )
  const { showToast } = useToast()

  if (!connection) return null

  const warning = disconnectWarning(connection, migrations)

  const disconnect = () => {
    setSubmitting(true)
    disconnectConnection({ connectionPublicId: connection.publicId }).then((result) => {
      setSubmitting(false)
      const payload = result.data?.disconnectCalendarConnection
      if (payload?.result === "success") {
        showToast("Your calendar has been disconnected")
        onDisconnected()
      } else {
        showToast({ content: payload?.errors?.[0] || "Could not disconnect this calendar", type: "error" })
      }
    })
  }

  return (
    <AnimatedModal
      header="Disconnect calendar"
      visible={Boolean(connection)}
      hideModal={onClose}
      showFooter={true}
      actionButtonCopy={submitting ? "Disconnecting…" : "Yes, disconnect"}
      actionButtonType="destructive"
      saveDisabled={submitting}
      onSave={disconnect}>
      <p>
        Are you sure you want to disconnect {providerLabel(connection.provider)}
        {connection.displayIdentity ? ` (${connection.displayIdentity})` : ""}? Heal.me will stop checking it for
        conflicts and stop adding bookings to it.
      </p>
      {warning && (
        <p className="mt-4 rounded-lg border border-red bg-red/5 p-3 text-sm" role="alert">
          {warning}
        </p>
      )}
    </AnimatedModal>
  )
}

export default DisconnectConfirmModal
